<template>
  <div class="ism-tab-body">
    <!-- Skeleton -->
    <template v-if="loading && !society">
      <div class="ism-stat-row" style="margin-bottom: 28px">
        <div
          v-for="i in 4"
          :key="i"
          class="ism-shimmer"
          style="height: 90px; border-radius: 14px; flex: 1; min-width: 100px"
        ></div>
      </div>
    </template>

    <template v-else>
      <!-- Stat Cards -->
      <div class="ism-stat-row">
        <div
          class="stat-card"
          style="--card-bar: #f26522; --icon-bg: rgba(242, 101, 34, 0.1)"
        >
          <div class="stat-card-top">
            <div class="stat-card-label">Houses</div>
            <div class="stat-card-icon">
              <v-icon color="#F26522" size="20">mdi-home-city-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ society.no_of_house || 0 }}</div>
          <div class="stat-card-delta">Total residential houses</div>
        </div>

        <div
          class="stat-card"
          style="--card-bar: #3b82f6; --icon-bg: rgba(59, 130, 246, 0.1)"
        >
          <div class="stat-card-top">
            <div class="stat-card-label">Shops</div>
            <div class="stat-card-icon">
              <v-icon color="#3b82f6" size="20">mdi-store-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ society.no_of_shop || 0 }}</div>
          <div class="stat-card-delta">Total commercial shops</div>
        </div>

        <div
          class="stat-card"
          style="--card-bar: #10b981; --icon-bg: rgba(16, 185, 129, 0.1)"
        >
          <div class="stat-card-top">
            <div class="stat-card-label">Type</div>
            <div class="stat-card-icon">
              <v-icon color="#10b981" size="20">mdi-domain</v-icon>
            </div>
          </div>
          <div class="stat-card-value" style="font-size: 16px">
            {{ residentType(society.type) }}
          </div>
          <div class="stat-card-delta">Society category</div>
        </div>

        <div
          class="stat-card"
          style="--card-bar: #8b5cf6; --icon-bg: rgba(139, 92, 246, 0.1)"
        >
          <div class="stat-card-top">
            <div class="stat-card-label">Currency</div>
            <div class="stat-card-icon">
              <v-icon color="#8b5cf6" size="20">mdi-currency-inr</v-icon>
            </div>
          </div>
          <div class="stat-card-value">
            {{ formatCurrency(society.currency) }}
          </div>
          <div class="stat-card-delta">Default billing currency</div>
        </div>
      </div>

      <!-- Main Info Sections (Matching AcceptRejectRequest.vue) -->
      <div class="ism-detail-grid">
        <!-- Left: Society Info -->
        <div class="ism-detail-panel">
          <div class="ism-panel-title">
            <v-icon size="16" color="var(--accent)" class="mr-2"
              >mdi-office-building-outline</v-icon
            >
            Society Information
          </div>

          <div class="ism-info-grid">
            <div class="ism-info-item ism-info-item--full">
              <div
                class="ism-info-icon"
                style="background: rgba(242, 101, 34, 0.1)"
              >
                <v-icon size="18" color="#F26522"
                  >mdi-map-marker-outline</v-icon
                >
              </div>
              <div class="ism-info-content">
                <span class="ism-info-label">Address</span>
                <span class="ism-info-value">{{ cleanAddress }}</span>
              </div>
            </div>
            <div class="ism-info-item">
              <div
                class="ism-info-icon"
                style="background: rgba(59, 130, 246, 0.1)"
              >
                <v-icon size="18" color="#3b82f6"
                  >mdi-city-variant-outline</v-icon
                >
              </div>
              <div class="ism-info-content">
                <span class="ism-info-label">Society Name</span>
                <span class="ism-info-value">{{ society.name || "—" }}</span>
              </div>
            </div>
            <!-- <div class="ism-info-item">
              <div class="ism-info-icon" style="background:rgba(16,185,129,0.1);"><v-icon size="18" color="#10b981">mdi-domain</v-icon></div>
              <div class="ism-info-content">
                <span class="ism-info-label">Resident Type</span>
                <span class="ism-info-value">{{ residentType(society.type) }}</span>
              </div>
            </div> -->
            <div class="ism-info-item">
              <div
                class="ism-info-icon"
                style="background: rgba(16, 185, 129, 0.1)"
              >
                <v-icon size="18" color="#10b981"
                  >mdi-check-circle-outline</v-icon
                >
              </div>
              <div class="ism-info-content" style="flex: 1">
                <span class="ism-info-label">Platform Status</span>
                <div
                  style="
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                  "
                >
                  <span
                    class="ism-info-value"
                    :style="{
                      color: society.is_active ? '#10b981' : '#ef4444',
                    }"
                  >
                    {{ society.is_active ? "Active" : "Inactive" }}
                  </span>
                  <v-switch
                    v-model="society.is_active"
                    @change="toggleStatus"
                    dense
                    hide-details
                    color="#10b981"
                    class="ma-0 pa-0"
                  />
                </div>
              </div>
            </div>
            <div class="ism-info-item">
              <div
                class="ism-info-icon"
                style="background: rgba(100, 116, 139, 0.1)"
              >
                <v-icon size="18" color="#64748b">mdi-crosshairs-gps</v-icon>
              </div>
              <div class="ism-info-content">
                <span class="ism-info-label">Coordinates</span>
                <span class="ism-info-value">{{
                  society.latitude && society.longitude
                    ? `${society.latitude}, ${society.longitude}`
                    : "N/A"
                }}</span>
              </div>
            </div>
            <div class="ism-info-item">
              <div
                class="ism-info-icon"
                style="background: rgba(242, 101, 34, 0.1)"
              >
                <v-icon size="18" color="#F26522">mdi-currency-inr</v-icon>
              </div>
              <div class="ism-info-content">
                <span class="ism-info-label">Currency</span>
                <span class="ism-info-value">{{
                  formatCurrency(society.currency)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Vertical Divider -->
        <div class="ism-detail-vdivider"></div>

        <!-- Middle: Contact Info -->
        <div class="ism-detail-panel">
          <div class="ism-panel-title">
            <v-icon size="16" color="var(--accent)" class="mr-2"
              >mdi-card-account-phone-outline</v-icon
            >
            Contact Information
          </div>

          <div class="ism-info-grid">
            <div class="ism-info-item ism-info-item--full">
              <div
                class="ism-info-icon"
                style="background: rgba(139, 92, 246, 0.1)"
              >
                <v-icon size="18" color="#8b5cf6"
                  >mdi-account-tie-outline</v-icon
                >
              </div>
              <div class="ism-info-content">
                <span class="ism-info-label">Contact Person</span>
                <span class="ism-info-value">{{
                  society.contact_person_name || "—"
                }}</span>
              </div>
            </div>
            <div class="ism-info-item ism-info-item--full">
              <div
                class="ism-info-icon"
                style="background: rgba(245, 158, 11, 0.1)"
              >
                <v-icon size="18" color="#f59e0b">mdi-phone-outline</v-icon>
              </div>
              <div class="ism-info-content">
                <span class="ism-info-label">Mobile Number</span>
                <span class="ism-info-value">{{
                  formatPhone(
                    society.country_code,
                    society.contact_person_number,
                  )
                }}</span>
              </div>
            </div>
            <div class="ism-info-item ism-info-item--full">
              <div
                class="ism-info-icon"
                style="background: rgba(239, 68, 68, 0.1)"
              >
                <v-icon size="18" color="#ef4444">mdi-email-outline</v-icon>
              </div>
              <div class="ism-info-content">
                <span class="ism-info-label">Email Address</span>
                <span class="ism-info-value" style="word-break: break-all">{{
                  society.contact_person_email || "—"
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Vertical Divider -->
        <div class="ism-detail-vdivider"></div>

        <!-- Right: Subscription Info -->
        <div class="ism-detail-panel">
          <div class="ism-panel-title">
            <v-icon size="18" color="var(--accent)" class="mr-2"
              >mdi-diamond-stone</v-icon
            >
            Subscription
          </div>

          <div class="ism-sub-content">
            <template v-if="currentPlan && currentPlan.expiration_date">
              <div class="ism-sub-plan-name">
                {{ currentPlan.plan ? currentPlan.plan.title : "Active" }} Plan
              </div>
              <div class="ism-sub-expiry">
                <v-icon
                  size="14"
                  color="var(--accent)"
                  style="margin-right: 6px"
                  >mdi-calendar-check-outline</v-icon
                >
                Expires: <strong>{{ formattedExpirationDate }}</strong>
              </div>
              <div class="ism-sub-divider"></div>

              <div
                class="ism-info-label"
                style="margin-bottom: 12px; font-size: 11px"
              >
                Extend Plan
              </div>
              <div style="display: flex; flex-direction: column; gap: 10px">
                <v-menu
                  ref="menu"
                  v-model="menu"
                  :close-on-content-click="false"
                  :return-value.sync="currentPlan.expiration_date"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedExpirationDate"
                      prepend-inner-icon="mdi-calendar-outline"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                      color="var(--accent)"
                      style="background: var(--bg-primary); border-radius: 8px"
                    />
                  </template>
                  <v-date-picker
                    v-model="currentPlan.expiration_date"
                    no-title
                    :min="minDate"
                    scrollable
                    color="#F26522"
                  >
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="var(--text-secondary)"
                      @click="menu = false"
                      >Cancel</v-btn
                    >
                    <v-btn
                      text
                      color="var(--accent)"
                      @click="$refs.menu.save(currentPlan.expiration_date)"
                      >OK</v-btn
                    >
                  </v-date-picker>
                </v-menu>
                <button
                  class="btn-accent"
                  style="width: 100%; justify-content: center; height: 42px"
                  @click="updateDateChange(currentPlan.expiration_date)"
                >
                  Update Expiry
                </button>
              </div>
            </template>
            <template v-else>
              <div class="ism-sub-empty">
                <div class="ism-empty-icon-wrap">
                  <v-icon size="36" color="var(--border-color2)"
                    >mdi-diamond-outline</v-icon
                  >
                </div>
                <p>No active subscription</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- Confirm Extend Modal -->
    <ConfirmModal
      :isOpen="dialogDate"
      type="primary"
      title="Confirm Extension"
      :message="`Extend the plan until ${getFormattedDate(newSelectedDate)}?`"
      confirmText="Yes, Extend"
      cancelText="Cancel"
      @confirm="extendDate"
      @cancel="dialogDate = false"
    />
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import ConfirmModal from "../../common/ConfirmModal.vue";

export default {
  name: "SocietyBasicInfo",
  components: { ConfirmModal },
  props: {
    society_id: String,
    society_prop: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: true,
      societyData: null,
      currentPlan: { expiration_date: null },
      menu: false,
      dialogDate: false,
      newSelectedDate: null,
      residentTypes: [
        { id: 1, name: "Residential" },
        { id: 2, name: "Commercial" },
        { id: 3, name: "Residential & Commercial" },
      ],
      countries: [],
    };
  },

  computed: {
    society() {
      return this.society_prop || this.societyData || {};
    },
    cleanAddress() {
      if (!this.society.name) return "—";
      return (
        [
          this.society.address,
          this.society.city,
          this.society.state,
          this.society.country,
        ]
          .map(v => {
            if (v === this.society.country && !isNaN(v)) {
              const c = this.countries.find(curr => curr.id == v);
              return c ? c.name : v;
            }
            return v;
          })
          .filter((v) => v && v !== "undefined" && v !== "null")
          .join(", ") || "—"
      );
    },
    formattedExpirationDate() {
      if (!this.currentPlan.expiration_date) return null;
      const d = new Date(this.currentPlan.expiration_date);
      const y = d.getFullYear();
      const m = (d.getMonth() + 1).toString().padStart(2, "0");
      const day = d.getDate().toString().padStart(2, "0");
      return `${day}-${m}-${y}`;
    },
    minDate() {
      if (
        this.currentPlan.expiration_date &&
        new Date(this.currentPlan.expiration_date) < new Date()
      ) {
        return new Date().toISOString().substr(0, 10);
      }
      return this.currentPlan.expiration_date || "";
    },
  },

  watch: {
    society_prop: {
      handler(val) {
        if (val) this.loading = false;
      },
      immediate: true,
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    residentType(type) {
      const t = this.residentTypes.find((r) => r.id == type);
      return t ? t.name : "N/A";
    },

    formatCurrency(c) {
      if (!c) return "—";
      const l = c.toLowerCase();
      if (l === "rs" || l === "inr") return "₹ INR";
      return c.toUpperCase();
    },

    formatPhone(code, number) {
      if (!number) return "—";
      const prefix = code ? (code.startsWith("+") ? code : "+" + code) : "";
      return `${prefix} ${number}`.trim();
    },

    getFormattedDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${d.getFullYear()}`;
    },

    updateDateChange(date) {
      this.newSelectedDate = date;
      this.dialogDate = true;
    },

    extendDate() {
      this.dialogDate = false;
      this.$axios
        .put(`superadmin/society-plan/${this.society_id}`, {
          expiration_date: this.newSelectedDate,
        })
        .then((res) => {
          if (res.data.status) {
            this.getUserPlan();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: 3000,
              message: res.data.message,
            });
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: 3000,
              message: res.data.message,
            });
          }
        });
    },

    toggleStatus() {
      this.$axios
        .post("superadmin/society/" + this.society_id + "/changeStatus")
        .then((res) => {
          const data = res.data;
          if (data.status) {
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: 3000,
              message: data.message,
            });
            this.$emit("update-society"); // Refresh parent data
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: 3000,
              message: data.message,
            });
            // If failed, revert the switch (though it's bound to society prop, so maybe fetch again)
            this.$emit("update-society");
          }
        });
    },

    getCountries() {
      this.$axios.get('society/countries').then(res => {
        if (res.data.status) {
          this.countries = res.data.data;
        }
      });
    },

    getSocietyDetailsFallback() {
      if (!this.society_prop) {
        this.loading = true;
        this.$axios
          .get(`superadmin/society/${this.society_id}`)
          .then((res) => {
            if (res.data.status) this.societyData = res.data.society;
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      }
    },

    getUserPlan() {
      this.$axios.get(`superadmin/user-plan/${this.society_id}`).then((res) => {
        if (res.data.status) this.currentPlan = res.data.current;
        this.loading = false;
      });
    },
  },

  mounted() {
    this.getSocietyDetailsFallback();
    this.getUserPlan();
    this.getCountries();
  },
};
</script>

<style scoped>
.ism-tab-body {
  padding: 28px;
  display: flex;
  flex-direction: column;
}

/* ── Stat Cards ───────────────────────────── */
.ism-stat-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

/* ── Detail Grid ─────────────────────────── */
.ism-detail-grid {
  display: flex;
  gap: 30px;
  padding: 0 0 30px 0;
}

.ism-detail-panel {
  flex: 1;
  min-width: 0;
}

.ism-detail-vdivider {
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent,
    var(--border-color2),
    transparent
  );
  flex-shrink: 0;
  opacity: 0.6;
}

.ism-panel-title {
  font-size: 12.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  border-bottom: 2px solid var(--border-color);
  padding-bottom: 10px;
}

/* ── Info Grid ──────────────────────────────── */
.ism-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
}

.ism-info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.2s;
}
.ism-info-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
  border-color: rgba(242, 101, 34, 0.18);
  background: var(--card-bg);
}
.ism-info-item--full {
  grid-column: 1 / -1;
}

.ism-info-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ism-info-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.ism-info-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
}

.ism-info-value {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
}

/* ── Subscription Content ─────────────────── */
.ism-sub-content {
  display: flex;
  flex-direction: column;
}
.ism-sub-plan-name {
  font-size: 18px;
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 6px;
}
.ism-sub-expiry {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 4px 10px;
  margin-bottom: 16px;
}
.ism-sub-divider {
  height: 1px;
  background: var(--border-color);
  margin: 16px 0;
}
.ism-sub-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  text-align: center;
}
.ism-empty-icon-wrap {
  width: 60px;
  height: 60px;
  background: var(--bg-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}
.ism-sub-empty p {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0;
}

/* ── Accent button ────────────────────────── */
.btn-accent {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, var(--accent), var(--accent-hover));
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 700;
  padding: 10px 18px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px var(--accent-glow);
  white-space: nowrap;
}
.btn-accent:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

/* ── Responsive ───────────────────────────── */
@media (max-width: 1200px) {
  .ism-detail-grid {
    flex-direction: column;
    gap: 20px;
  }
  .ism-detail-vdivider {
    width: 100%;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      var(--border-color2),
      transparent
    );
  }
}
@media (max-width: 600px) {
  .ism-tab-body {
    padding: 18px;
  }
  .ism-stat-row {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .ism-detail-grid {
    padding: 20px;
  }
  .ism-info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
